import {
  Autocomplete,
  AutocompletePropsSizeOverrides,
  AutocompleteRenderGetTagProps,
  Box,
  CircularProgress,
  FilterOptionsState,
  ListItemText,
  Stack,
  SxProps,
  TextField,
  TextFieldVariants,
  Theme,
  Typography,
} from '@mui/material'
import { OverridableStringUnion } from '@mui/types'
import { useTranslate } from '@tolgee/react'
import { KeyboardEvent, MouseEvent, ReactNode, useCallback, useEffect, useState } from 'react'
import { useMinimalsTheme } from '../../../hooks'
import { PlusIcon } from '../../icons'
import { CustomInputAdornment } from '../form/CustomInputAdornment'
import { HighlightedText } from '../highlightedText/HighlightedText'

export type TomAutoCompleteProps<T, M extends boolean = false> = {
  id?: string
  label?: string
  placeholder?: string
  entityDisplayValue?: keyof T
  error?: boolean
  helperText?: ReactNode
  disabled?: boolean
  loading?: boolean
  required?: boolean
  dataCy?: string
  value?: M extends true ? T[] : T | null
  defaultValue?: M extends true ? T[] : T
  startAdornment?: JSX.Element
  displayPopupIcon?: boolean
  disableClearable?: boolean
  enablePortal?: boolean
  multiple?: M
  sx?: SxProps<Theme>
  size?: OverridableStringUnion<'small' | 'medium', AutocompletePropsSizeOverrides>
  variant?: TextFieldVariants
  optionalLabel?: (option: T) => JSX.Element | null
  onAddNew?: (e?: MouseEvent | KeyboardEvent, inputValue?: string) => void
  onClear?: () => void
  getOptionLabel?: (option: T) => string
  getOptionSubtitle?: (option: T) => string | undefined | null
  getOptionDisabled?: (option: T) => boolean
  renderOption?: (props: any, option: T) => JSX.Element
  onChangeEntity?: (entity?: M extends true ? T[] : T | null | undefined) => void
  onChangeFilter?: (filter?: string) => void
  validate?: () => void
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[]
  renderTags?: (value: T[], getTagProps: AutocompleteRenderGetTagProps) => ReactNode
}

type Props<T, M extends boolean = false> = {
  options: T[]
  entityKey?: keyof T
} & TomAutoCompleteProps<T, M>

export const TomAutoComplete = <T, M extends boolean = false>(props: Props<T, M>) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const {
    id,
    options,
    error,
    helperText,
    label,
    placeholder,
    loading,
    required,
    dataCy,
    displayPopupIcon,
    startAdornment,
    entityDisplayValue,
    entityKey,
    value: controlledValue,
    defaultValue,
    enablePortal,
    multiple,
    optionalLabel,
    onAddNew,
    onClear,
    getOptionLabel,
    getOptionSubtitle,
    getOptionDisabled,
    renderOption,
    onChangeEntity,
    onChangeFilter,
    validate,
    filterOptions,
    renderTags,
  } = props

  const isControlled = controlledValue !== undefined
  const [inputValue, setInputValue] = useState<string | undefined>()
  const [resolvedValue, setResolvedValue] = useState<T | T[] | null | undefined>(defaultValue)

  useEffect(() => {
    setTimeout(() => {
      setResolvedValue(defaultValue)
    }, 100)
  }, [defaultValue])

  const getDisplayValue = useCallback(
    (entity: T) => {
      if (getOptionLabel) {
        return getOptionLabel(entity)
      } else if (entityDisplayValue) {
        return `${entity[entityDisplayValue]}`
      } else if (entityKey) {
        return `${entity[entityKey]}`
      } else if (typeof entity === 'string') {
        return t(entity, entity)
      } else {
        return `${entity}`
      }
    },
    [entityDisplayValue, entityKey, t, getOptionLabel],
  )

  // Only define renderOptionItems if T has an "id" property of type number
  // This is a TypeScript type guard for better type safety
  function hasNumericId(option: any): option is { id: number } {
    return option && typeof option.id === 'number'
  }

  const defaultRenderOption = useCallback(
    (props: any, option: T) => {
      const entityOption = (
        <>
          <Stack>
            <Typography variant='body1'>
              <HighlightedText highlight={inputValue} color={theme.palette.info.main}>
                {getDisplayValue(option)}
              </HighlightedText>
            </Typography>
            {getOptionSubtitle && (
              <Typography variant='caption' color='secondary'>
                {getOptionSubtitle(option)}
              </Typography>
            )}
          </Stack>
          {optionalLabel?.(option)}
        </>
      )

      if (!hasNumericId(option) || !onAddNew) {
        // fallback to default rendering if no numeric id
        return (
          <Box component='li' p={0.4} {...props} key={`${entityKey ? option[entityKey] : option}`}>
            {entityOption}
          </Box>
        )
      }

      const addNewOption = (
        <Box
          onClick={e => onAddNew(e, inputValue)}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingTop: 0.6,
            paddingBottom: 0.6,
          }}
        >
          <ListItemText>{t('addNew', 'Add new')}</ListItemText>
          <PlusIcon />
        </Box>
      )

      const optionItem = (
        <Box component='li' p={0.4} {...props} key={`location_${option.id}`}>
          <Stack
            flexDirection='row'
            justifyContent='space-between'
            alignItems='center'
            gap={1}
            width='100%'
          >
            {option.id === 0 ? addNewOption : entityOption}
          </Stack>
        </Box>
      )

      return optionItem
    },
    [
      inputValue,
      theme.palette.info.main,
      getDisplayValue,
      getOptionSubtitle,
      optionalLabel,
      onAddNew,
      t,
      entityKey,
    ],
  )

  return (
    <Autocomplete
      {...props}
      id={id}
      autoHighlight
      disablePortal={!enablePortal}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={entity => {
        return getDisplayValue(entity)
      }}
      options={options}
      noOptionsText={
        inputValue?.length
          ? t('noResultsFound', 'No results found')
          : t('startTypingToSearch', 'Start typing to search...')
      }
      isOptionEqualToValue={(option: T, value: T) =>
        entityKey ? option[entityKey] === value[entityKey] : option === value
      }
      value={
        (multiple
          ? (isControlled ? controlledValue : resolvedValue) ?? []
          : (isControlled ? controlledValue : resolvedValue) ?? null) as M extends true
          ? T[]
          : T | null
      }
      disableCloseOnSelect={multiple}
      onChange={(_, entity) => {
        if (!entity) onClear?.()

        onChangeEntity?.(entity as M extends true ? T[] : T | null | undefined)
        if (!isControlled) setResolvedValue(entity)
      }}
      renderTags={renderTags}
      renderOption={renderOption ? renderOption : defaultRenderOption}
      renderInput={params => (
        <TextField
          {...params}
          {...props}
          required={required}
          data-cy={dataCy}
          label={label}
          placeholder={
            renderTags && multiple && Array.isArray(controlledValue) && controlledValue.length > 0
              ? ''
              : placeholder
          }
          error={error}
          helperText={helperText}
          onBlur={() => {
            const currentValue = isControlled ? controlledValue : resolvedValue
            if (!currentValue) {
              setInputValue(undefined)
              onChangeFilter?.(undefined)
            }
          }}
          onChange={e => {
            setInputValue(e.target.value)
            onChangeFilter?.(e.target.value)
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: startAdornment ? (
                <Box display={'flex'}>
                  <CustomInputAdornment
                    position='start'
                    color='secondary'
                    padding={`0 ${theme.customSpacing.m}`}
                  >
                    {startAdornment}
                  </CustomInputAdornment>
                  <Box sx={{ marginLeft: '20px' }}>{params.InputProps.startAdornment}</Box>
                </Box>
              ) : (
                params.InputProps.startAdornment
              ),
              endAdornment: displayPopupIcon ? (
                params.InputProps.endAdornment
              ) : (
                <>
                  {loading && <CircularProgress size={20} />}
                  {!loading && multiple && params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      onBlur={validate}
      filterOptions={filterOptions}
    />
  )
}
